import React, { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { makeStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import Backdrop from '@material-ui/core/Backdrop';
import { showSuccessSnackbar } from 'modules/snackbar';
import NewButton from 'components/molecules/NewButton';
import { useDispatch } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  LinearProgressBox: {
    width: '40%',
  },
  LinearProgressBoxText: {
    color: '#fff',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 10,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

const MbUpload = (props) => {
  const { name, label, callBack = (f) => f, uploading = false } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const initProgress = {
    progress: 0,
    buffer: 10,
  };
  const batchCount = 30;
  const apiDelay = 4000;
  const totalVal = apiDelay * batchCount;
  const [BackdropOpen, setBackdropOpen] = useState(false);
  const [progress, setProgress] = useState(initProgress.progress);
  const [buffer, setBuffer] = useState(initProgress.buffer);
  const [statusText, setStatusText] = useState('Checking files ...');
  const timeout = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const progressRef = useRef();

  progressRef.current = (per) => {
    if (progress < 100) {
      const diff = per + Math.random() * 20;
      setProgress(per);
      setBuffer(diff);
    } else {
      setProgress(100);
      setBuffer(100);
    }
  };

  useEffect(() => {
    const uploadBuffer = async () => {
      return new Promise((resolve) => {
        let cnt = 0;
        const timer = setInterval(() => {
          cnt++;
          const thisPer = ((apiDelay * cnt) / totalVal) * 100;
          progressRef.current(thisPer);
          if (thisPer === 100) {
            clearInterval(timer);
          }
        }, 100);
        resolve();
      });
    };
    if (uploading === true) {
      uploadBuffer();
    } else if (uploading === false && progressRef.current < 100) {
      progressRef.current(100); // eslint-disable-next-line
    }
    timeout(1000);
    setBackdropOpen(uploading); // eslint-disable-next-line
  }, [uploading]);

  /**
   * 스넥바컨트롤함수
   * @type {Function}
   */
  const handleSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  /**
   * 백드랍 핸들함수
   * @param open
   */
  const handledBackDrop = (open) => {
    setBackdropOpen(open);
  };

  const handleLinearProgress = (reset) => {
    if (reset) {
      setProgress(initProgress.progress);
      setBuffer(initProgress.buffer);
    }
  };

  /**
   * 파일업로드 검증
   * @param formData
   * @param taskId
   * @param cycle
   * @returns {Promise<void>}
   */
  const callSanitoxBatch = async (formData, taskId, cycle) => {
    cycle += 1;
    const apiKey = '118111892a394ee4903a32115f1f8eeb';
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
        Authorization: 'Token ' + apiKey,
      },
    };
    try {
      const response = await axios.get(
        'https://sanitox.mobon.net/api/v1/tasks/' + taskId + '/',
        config
      );
      const { data } = response;
      const thisPer = ((apiDelay * cycle) / totalVal) * 100;
      progressRef.current(thisPer);
      if (data.task.state === 'PENDING') {
        if (cycle < batchCount) {
          await timeout(apiDelay);
          await callSanitoxBatch(formData, taskId, cycle);
        } else {
          handleSnackbar({
            message:
              '무해화 오류입니다. 업로드 파일을 다시 한 번 확인해 주세요. 문제가 지속적으로 발생할 경우 시스템 관리자에게 문의해 주세요.',
            option: {
              type: 'error',
            },
          });
          return false;
        }
      } else if (
        data.task.state === 'SUCCESS' &&
        (data.task.result.code === 200 ||
          data.task.result.code === 300 ||
          data.task.result.code === 500)
      ) {
        progressRef.current(100);
        setStatusText('Success');
        await timeout(1000);
      } else if (
        data.task.state === 'SUCCESS' &&
        data.task.result.code === 700
      ) {
        handleSnackbar({
          message:
            '백신 검사결과 바이러스 의심 파일로 확인되어 업로드가 거절 되었습니다.',
          option: {
            type: 'error',
          },
        });
        return false;
      } else if (
        data.task.state === 'SUCCESS' &&
        data.task.result.code === 710
      ) {
        handleSnackbar({
          message:
            'Virustotal 검사결과 바이러스 의심 파일로 확인되어 업로드가 거절 되었습니다.',
          option: {
            type: 'error',
          },
        });
        return false;
      } else {
        handleSnackbar({
          message: data.task.result.detail,
          option: {
            type: 'error',
          },
        });
        return false;
      }
    } catch (e) {
      console.log(e);
    }
  };

  /**
   * 파일업로드 스캔
   * @param formData
   * @returns {Promise<void>}
   */
  const callSanitoxScan = async (formData) => {
    setProgress(0);
    setBuffer(10);
    const apiKey = '118111892a394ee4903a32115f1f8eeb';
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
        Authorization: 'Token ' + apiKey,
      },
    };
    let resultData = {};
    try {
      const response = await axios.post(
        'https://sanitox.mobon.net/api/v1/tasks/scan/',
        formData,
        config
      );
      resultData = {
        statusText: response.statusText,
        taskId: response.data.task_id,
        status: response.status,
      };

      return resultData;
    } catch (e) {
      console.log(e);
      resultData = {
        statusText: 'FAILED',
      };
      return resultData;
    }
  };

  /**
   * 파일업로드 & 무해화체크
   * @param e
   * @param target
   * @returns {Promise<boolean>}
   */
  const handleFileInput = async (e, target) => {
    if (e.target.files[0] === undefined) return false;

    handledBackDrop(true);
    const formData = new FormData();
    formData.append('upload_file', e.target.files[0]);
    const { taskId } = await callSanitoxScan(formData);
    await callSanitoxBatch(formData, taskId, 0);
    await timeout(500);
    setStatusText('Uploading files ...');
    handleLinearProgress(true);
    await timeout(500);
    await callBack(taskId);
  };

  return (
    <>
      <NewButton upLoad changeEvent={(e) => handleFileInput(e, name)}>
        {label || '파일업로드'}
      </NewButton>

      {BackdropOpen && (
        <Backdrop className={classes.backdrop} open={BackdropOpen}>
          <div className={classes.LinearProgressBox}>
            <div className={classes.LinearProgressBoxText}>{statusText}</div>
            <LinearProgress
              variant="buffer"
              value={progress}
              valueBuffer={buffer}
            />
          </div>
        </Backdrop>
      )}
    </>
  );
};

export default MbUpload;
